import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import type { RayEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { EntityObjectUtils } from '@/plugins/cad/module/CADModule/object/entityObject/entityObjectUtils'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { BaseObjectManager, HighlightMode } from './BaseObjectManager'

/** 射线对象管理器 */
export class RayObjectManager extends BaseObjectManager<RayEntity, Line2> {
  // entity的原材质
  private entityOriginalMaterialMap: Map<string, LineMaterial> = new Map()

  protected createObject(entity: RayEntity): Line2 {
    const geometry = this.createGeometry(entity)
    const material = this.getCachedMaterial(entity)
    const object = createCadLine2Object(geometry, material)
    object.computeLineDistances()
    object.geometry.computeBoundingBox()
    object.geometry.computeBoundingSphere()

    this.entityOriginalMaterialMap.set(entity.id, object.material)

    return object
  }

  protected onUpdateGeometry(entity: RayEntity): void {
    const object = this.getEntityObject(entity)
    if (!object) {
      return
    }
    const points = EntityObjectUtils.getRaySamplePoints(entity)
    const vertices: number[] = points.flatMap((p) => [p.x, p.y, 0])
    object.geometry.setPositions(vertices)
    object.geometry.attributes.position.needsUpdate = true
    object.computeLineDistances()
    object.geometry.computeBoundingBox()
    object.geometry.computeBoundingSphere()
  }

  protected onUpdateMaterial(entity: RayEntity): void {
    const object = this.getEntityObject(entity)
    if (!object) {
      return
    }
    this.disposeMaterialFromObject(object)
    const material = this.getCachedMaterial(entity)
    object.material = material
    material.needsUpdate = true

    this.entityOriginalMaterialMap.set(entity.id, object.material)
    this.updateEntityHighlight(entity)
  }

  protected handleEntityHighlight(entity: RayEntity, mode: HighlightMode): void {
    const object = this.getEntityObject(entity)
    if (!object) {
      return
    }
    if (mode === HighlightMode.None) {
      object.material = this.entityOriginalMaterialMap.get(entity.id)!
      object.material.needsUpdate = true
      object.computeLineDistances()
    } else {
      const material =
        mode === HighlightMode.Hover
          ? this.materialManager.getCachedHoverMaterial(entity)
          : this.materialManager.getCachedSelectMaterial(entity)
      object.material = material
      object.material.needsUpdate = true
      object.computeLineDistances()
    }
  }

  protected disposeObject(object: Line2): void {
    object.geometry?.dispose?.()
    this.entityOriginalMaterialMap.delete(object.userData.entity.id)
  }

  private createGeometry(entity: RayEntity) {
    const points = EntityObjectUtils.getRaySamplePoints(entity)
    return DrawUtils.getLineGeometryByPoints(points)
  }
}
